/**
 * XState integration hook.
 *
 * Provides the FSM actor and derived display state to React components.
 * Uses useSyncExternalStore for React 19 compatibility with the XState v5 actor.
 *
 * The FSM is a parallel state machine with 7 regions (request, playback,
 * input, micMode, speakerMode, connection, activation). This hook derives
 * a simplified DisplayState from all parallel regions for UI rendering.
 */

import { useSyncExternalStore, useCallback, useMemo } from 'react';
import { createActor } from 'xstate';
import {
  voiceAppMachine,
  deriveDisplayState,
  getStatusText,
} from '../fsm/machine';
import type {
  DisplayState,
  MachineContext,
  MachineEvent,
} from '../fsm/machine';
import { remoteLog } from '../utils/remoteLog';

// ── Singleton Actor ──

/** Create a singleton FSM actor (one per app lifecycle) */
const actor = createActor(voiceAppMachine);
actor.start();

// Track previous state for logging transitions
let _prevFsmValue: Record<string, string> | null = null;

// Log every FSM transition
actor.subscribe((snapshot) => {
  const newValue = snapshot.value as Record<string, string>;
  if (_prevFsmValue !== null) {
    // Check if any region changed
    const changedRegions: Record<string, { from: string; to: string }> = {};
    for (const region of Object.keys(newValue)) {
      if (newValue[region] !== _prevFsmValue[region]) {
        changedRegions[region] = { from: _prevFsmValue[region], to: newValue[region] };
      }
    }
    if (Object.keys(changedRegions).length > 0) {
      remoteLog('fsm', 'transition', { regions: changedRegions });
    }
    // Log context toggle changes
    const ctx = snapshot.context;
    const prevCtx = _prevFsmValue['_ctx'] ? JSON.parse(_prevFsmValue['_ctx']) : null;
    if (prevCtx) {
      if (ctx.voiceInterrupt !== prevCtx.voiceInterrupt) {
        remoteLog('fsm', 'context_change', { field: 'voiceInterrupt', value: ctx.voiceInterrupt });
      }
      if (ctx._micMode !== prevCtx._micMode) {
        remoteLog('fsm', 'context_change', { field: '_micMode', value: ctx._micMode });
      }
      if (ctx._speakerMode !== prevCtx._speakerMode) {
        remoteLog('fsm', 'context_change', { field: '_speakerMode', value: ctx._speakerMode });
      }
    }
    // Store context snapshot for next comparison
    _prevFsmValue = { ...newValue, _ctx: JSON.stringify({ voiceInterrupt: snapshot.context.voiceInterrupt, _micMode: snapshot.context._micMode, _speakerMode: snapshot.context._speakerMode }) };
  } else {
    _prevFsmValue = { ...newValue, _ctx: JSON.stringify({ voiceInterrupt: snapshot.context.voiceInterrupt, _micMode: snapshot.context._micMode, _speakerMode: snapshot.context._speakerMode }) };
    remoteLog('fsm', 'init', { value: newValue });
  }
});

// ── Hook Return Type ──

export interface UseFSMReturn {
  /** Send an event to the FSM */
  send: (event: MachineEvent) => void;
  /** Current display state (derived from parallel regions) */
  displayState: DisplayState;
  /** Status text for the current display state */
  statusText: string;
  /** Raw FSM snapshot value (all parallel region states) */
  value: Record<string, string>;
  /** FSM context */
  context: MachineContext;
  /** Helper: is the app activated? */
  isActivated: boolean;
  /** Helper: is a request in progress (thinking or streaming)? */
  isRequestActive: boolean;
  /** Helper: is TTS playing? */
  isPlaying: boolean;
  /** Helper: is the input region processing? */
  isProcessing: boolean;
  /** Helper: is connected to gateway? */
  isConnected: boolean;
  /** Helper: is mic muted (command-only mode)? */
  isMicMuted: boolean;
  /** Helper: is speaker muted? */
  isSpeakerMuted: boolean;
}

/**
 * Hook for XState FSM integration.
 *
 * Uses useSyncExternalStore for tear-free reads of the actor snapshot.
 * The actor is a module-level singleton — all components share the same FSM.
 */
export function useFSM(): UseFSMReturn {
  const subscribe = useCallback((callback: () => void) => {
    const sub = actor.subscribe(callback);
    return () => sub.unsubscribe();
  }, []);

  const getSnapshot = useCallback(() => actor.getSnapshot(), []);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot);

  const value = snapshot.value as Record<string, string>;
  const context = snapshot.context;
  const displayState = deriveDisplayState(value);
  const statusText = getStatusText(displayState);

  const send = useMemo(
    () => (event: MachineEvent) => actor.send(event),
    [],
  );

  return {
    send,
    displayState,
    statusText,
    value,
    context,
    isActivated: value.activation === 'active',
    isRequestActive: value.request !== 'idle',
    isPlaying: value.playback === 'playing',
    isProcessing: value.input === 'processing',
    isConnected: value.connection === 'connected',
    isMicMuted: value.micMode === 'commandOnly',
    isSpeakerMuted: value.speakerMode === 'muted',
  };
}

/**
 * Get the FSM actor directly (for non-React code like chat/voice modules).
 * Useful when you need to send events or read state outside a React component.
 */
export function getFSMActor() {
  return actor;
}
